import {
  ToastProviderProps,
  defineStyleConfig,
  extendTheme,
} from '@chakra-ui/react';
import '@fontsource/open-sans/600.css';
import { checkboxTheme } from './checkbox';

const theme = extendTheme({
  fonts: {
    heading: `'Open Sans' | 'verdana' | 'report' | 'public-sans'`,
    body: `'Open Sans' | 'verdana' | 'report' | 'public-sans'`,
  },
  colors: {
    gray: {
      25: '#F8F9FC',
    },
    text: {
      default: '#2a2b2d',
    },
    border: {
      default: '#d6e2e6',
    },
    primaryBlue: {
      500: '#1da8d5',
      600: '#1785a8',
      700: '#015e99',
    },
    lightBlue: {
      500: '#3ab3da',
      600: '#489eca',
      700: '#3a9cfa',
    },
    blue: {
      600: '#004dc2',
      900: '#014069',
    },
    unit: {
      default: '#1b74ba',
      mixed: '#656565',
      1: '#f05e5b',
      2: '#f79648',
      3: '#febc22',
      4: '#ccde62',
      5: '#7ac058',
      6: '#2a6b46',
      7: '#48bfaa',
      8: '#85aedc',
      9: '#3465af',
      10: '#223264',
      11: '#b298c8',
      12: '#812775',
      13: '#da4587',
      14: '#5e051b',
      15: '#b52024',
      16: '#f9b2b3',
      17: '#fdcaa7',
      18: '#f05e5b',
      19: '#f79648',
      20: '#febc22',
      21: '#ccde62',
      22: '#7ac058',
      23: '#2a6b46',
      24: '#48bfaa',
      25: '#85aedc',
      26: '#3465af',
      27: '#223264',
      28: '#b298c8',
      29: '#812775',
      30: '#da4587',
      31: '#5e051b',
      32: '#b52024',
      33: '#f9b2b3',
      34: '#fdcaa7',
    },
    prenext: {
      greenLight: '#21ba45',
      greenDark: '#009824',
    },
    reviewNumber: {
      correct: '#178730',
      incorrect: '#E61C0F',
    },
    edgexl: {
      greyLight: '#a7a6a6',
      iconColor: '#777777',
    },
    reports: {
      green: '#7bbb4a',
      yellow: '#fbbd2c',
      red: '#cd373d',
      grey: '#707070',
      greyLight: '#c5c5c6',
      blue: '#139fc9',
    },
    onramp: {
      platinum: '#DEE2E6',
      aurometalsaurus: '#6c757d',
      lightsilver: '#D9D9D9',
      lightgray: '#CED4DA',
    },

    green: {
      light: '#F1F8EC',
    },
    info: {
      100: '#F4FAFFFF',
    },

    reportAvatarBgColor: {
      0: '#005D92',
      1: '#064064',
      2: '#102B4E',
      3: '#0142BD',
      4: '#00164F',
      5: '#2146A5',
      6: '#041E62',
      7: '#253BAB',
      8: '#001AFF',
      10: '#0C0465',
      11: '#0E0384',
      12: '#002689',
      13: '#001683',
      14: '#4F20FF',
      15: '#200394',
      16: '#B97EF3',
      17: '#7E28C2',
      18: '#5E0098',
      19: '#98009B',
      20: '#B1EBC0',
      21: '#DAF2D8',
      22: '#D8ECFB',
    },

    reportColor: {
      0: '#FFFFFF',
      1: '#FFC1BE',
      4: '#FEFFB6',
      5: '#FFEE14',
      6: '#FFAE64',
      8: '#C8F8FF',
      11: '#37FFDB',
      12: '#FFA9A9',
      13: '#8BE2FA',
      15: '#FDC9C9',
      16: '#000428',
      18: '#FFC1C1',
      20: '#0036B7',
      21: '#000000',
    },

    reportTableColor: {
      bollicelli: '#D1E4ED',
      aquaHaza: '#F0F4F6',
      athensGray: '#E9ECEF',
      vanillaIce: '#F2DBDD',
      shiraz: '#AB0F1E',
    },
  },

  fontSizes: {
    edgexl: {
      list: '.75rem',
    },
  },
  components: {
    Checkbox: checkboxTheme,

    Badge: defineStyleConfig({
      variants: {
        greenBadge: {
          background: 'green.light',
          border: '1px solid',
          borderColor: 'olive.100',
          fontWeight: 400,
          textTransform: 'uppercase',
          color: 'olive.100',
          px: 2,
          py: 0.5,
          borderRadius: '4px',
          fontSize: '0.625rem',
        },
        adminBadge: {
          background: 'info.100',
          border: '1px solid',
          borderColor: 'blue.500',
          fontWeight: 400,
          textTransform: 'uppercase',
          color: 'blue.500',
          px: 2,
          py: 0.5,
          borderRadius: '4px',
          fontSize: '0.625rem',
        },
        studentBadge: {
          background: '#EFEDFF',
          border: '1px solid',
          borderColor: 'calendar.violet.1',
          fontWeight: 400,
          textTransform: 'uppercase',
          color: 'calendar.violet.1',
          px: 2,
          py: 0.5,
          borderRadius: '4px',
          fontSize: '0.625rem',
        },
      },
    }),

    Button: defineStyleConfig({
      defaultProps: {
        colorScheme: 'blue',
      },
      baseStyle: {
        fontWeight: 'bold',
        fontSize: { base: 'sm', md: 'md' },
      },
      variants: {
        reset: (props) => ({
          ...theme.components.Button.variants.ghost(props),
          height: '16px',
          alignSelf: 'flex-end',
          px: 0,
          py: 1,
          _hover: { backgroundColor: 'transparent' },
        }),
        black: {
          color: 'white',
          background: 'gray.700',
          _hover: {
            background: 'gray.800',
          },
          _active: {
            background: 'gray.900',
          },
        },
        darkGray: {
          color: 'white',
          background: 'gray.500',
          _hover: {
            background: 'gray.600',
          },
          _active: {
            background: 'gray.700',
          },
        },
        solidDark: {
          backgroundColor: 'white',
          color: 'blue.500',
          _hover: {
            backgroundColor: 'primary.100',
            _disabled: {
              backgroundColor: 'primary.400',
              color: 'primary.100',
              opacity: 1,
            },
          },
          _focusVisible: {
            backgroundColor: 'primary.100',
            boxShadow: '0 0 0 4px var(--chakra-colors-blue-600)',
            textDecoration: 'none',
            zIndex: 2,
          },
          _active: {
            backgroundColor: 'primary.200',
          },
          _disabled: {
            backgroundColor: 'primary.400',
            color: 'primary.100',
            opacity: 1,
          },
        },
        invertedBlue: {
          color: 'white',
          background: 'blue.700',
          _hover: {
            background: 'blue.600',
            _disabled: {
              background: 'blue.700',
            },
          },
          _active: {
            background: 'blue.500',
          },
        },
        darkBlue: {
          color: 'white',
          background: 'blue.600',
          _hover: {
            background: 'blue.700',
            _disabled: {
              background: 'blue.600',
            },
          },
          _active: {
            background: 'blue.500',
          },
        },
        softBlueOutline: (props) => ({
          ...theme.components.Button.variants?.outline(props),
          color: 'blue.600',
          background: 'white',
          border: '2px solid blue',
          _hover: {
            background: 'gray.50',
          },
          _active: {
            background: 'gray.100',
          },
        }),
        invertedBlueOutline: (props) => ({
          ...theme.components.Button.variants?.outline(props),
          color: 'black',
          _hover: {
            background: 'gray.50',
          },
          _active: {
            background: 'gray.100',
          },
        }),
      },
    }),

    Heading: defineStyleConfig({
      baseStyle: {
        fontSize: { base: 'md', md: 'xl' },
        color: 'blue.600',
        fontWeight: 'bold',
      },
    }),

    Alert: defineStyleConfig({
      baseStyle: {
        container: {
          borderRadius: 'md',
          p: 4,
        },
      },
      variants: {
        info: {
          container: {
            bgColor: 'blue.100',
            color: 'blue.800',
          },
        },
        error: {
          container: {
            bgColor: 'red.100',
            color: 'red.700',
          },
        },
        success: {
          container: {
            bgColor: 'green.100',
            color: 'green.700',
          },
        },
      },
    }),

    Text: defineStyleConfig({
      baseStyle: {
        fontSize: { base: 'sm', md: 'md' },
      },

      variants: {
        lightBlueH2: {
          color: 'lightBlue.600',
          fontWeight: 'bold',
          fontSize: '2xl',
          as: 'h2',
        },

        n1: {
          fontSize: 'md',
          lineHeight: '1.625rem',
          fontWeight: 700,
          color: 'blue.600',
        },
      },
    }),

    Link: defineStyleConfig({
      baseStyle: {
        fontSize: { base: 'sm', md: 'md' },
      },
      variants: {
        inPopover: {
          px: 3,
          py: 2,
          color: 'white',
          fontSize: 'sm',
          w: '100%',
          textDecoration: 'none',
          _hover: {
            textDecoration: 'none',
            background: 'primaryBlue.500',
          },
        },
      },
    }),
  },
});

export const toastOptions: ToastProviderProps = {
  defaultOptions: { position: 'top' },
};

export default theme;
